import { CSRF_STATE_KEY, updateStoredTokens } from "../hooks/use-auth-tokens.js";
import { fetchAuthToken } from "./tokens.js";
import { generatePkceConfig } from "./pkce.js";

//#region src/client/util/login.ts
const AUTH_FLOW = {
	Deeplink: 1,
	Employer: 2,
	CreateAccount: 3,
	SignIn: 4,
	UpdateProfile: 5
};
const initiateLoginChallenge = async ({ mustReauthenticate = false, authUrl, clientId, redirectUri, returnUrl, state }) => {
	const { challenge, codeVerifier } = await generatePkceConfig();
	const authState = {
		...state,
		codeVerifier,
		currentUrl: returnUrl,
		mustReauthenticate,
		stateKey: CSRF_STATE_KEY
	};
	const payload = window.btoa(JSON.stringify(authState));
	localStorage.setItem(CSRF_STATE_KEY, payload);
	const newUrl = new URL(authUrl);
	newUrl.searchParams.set("client_id", clientId);
	newUrl.searchParams.set("redirect_uri", redirectUri);
	newUrl.searchParams.set("response_type", "code");
	newUrl.searchParams.set("state", payload);
	newUrl.searchParams.set("code_challenge_method", "S256");
	newUrl.searchParams.set("code_challenge", challenge);
	window.location.replace(decodeURIComponent(newUrl.toString()));
};
const validateIncomingState = (state) => {
	try {
		const decodedState = JSON.parse(window.atob(state));
		const localState = localStorage.getItem(CSRF_STATE_KEY);
		if (!localState) return { error: "Login state is missing or was already used" };
		const decodedLocalState = JSON.parse(window.atob(localState));
		if (decodedState.currentUrl === decodedLocalState.currentUrl && decodedState.codeVerifier === decodedLocalState.codeVerifier) {
			localStorage.removeItem(CSRF_STATE_KEY);
			return { authState: decodedLocalState };
		}
	} catch {}
	return { error: "CSRF state mismatch during login" };
};
/**
* The code exchange for this page, in progress or complete.
* An authorization code is single use. If the same code is exchanged twice,
* Ory revokes the tokens from the first exchange too, so every caller with
* the same code shares one request. The entry stays after the promise
* settles, because a later call with the same code must not reach the token
* endpoint either.
*
* @see MYPT-1122
*/
let activeLoginExchange = null;
const exchangeLoginCode = async ({ clientId, code, redirectUri, state = "", tokenUrl }) => {
	if (!code) return { error: "response code is missing to get the tokens" };
	const stateValidation = validateIncomingState(state);
	if ("error" in stateValidation) return { error: stateValidation.error };
	const { authState } = stateValidation;
	const jwt = await (await fetchAuthToken(code, clientId, tokenUrl, authState.codeVerifier, redirectUri)).json();
	if ("error" in jwt) return {
		error: jwt.error,
		errorDescription: jwt.error_description
	};
	if (jwt.access_token && jwt.refresh_token) {
		updateStoredTokens({
			accessToken: jwt.access_token,
			refreshToken: jwt.refresh_token
		});
		return { nextUrl: authState.currentUrl };
	}
	return { error: "access or refresh token is missing" };
};
const processLoginResponse = (props) => {
	if (activeLoginExchange?.code === props.code) return activeLoginExchange.promise;
	const promise = exchangeLoginCode(props);
	activeLoginExchange = {
		code: props.code,
		promise
	};
	return promise;
};

//#endregion
export { AUTH_FLOW, initiateLoginChallenge, processLoginResponse };